import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ListSkeleton } from '@/components/ui/skeleton';
import { useActivityRefreshStore } from '../store';
import { appendUnique } from '../lib/feedReducer';
import type { ActivityFeedPage, ActivityLogDto } from '../types';
import { ActivityRow } from './ActivityRow';
import { ActivityDetailDrawer } from './ActivityDetailDrawer';

interface ActivityListProps {
  /**
   * Fetches one keyset page. `cursor` is null for the first page — which is also the only response that
   * carries `totalCount`. Must be memoized by the caller (`useCallback`): its identity is what resets the
   * list back to page one, so an inline arrow refetches on every parent render.
   */
  fetcher: (cursor: { occurredAtUtc: string; id: number } | null) => Promise<ActivityFeedPage>;
  /** Optional empty-state message override. */
  emptyMessage?: string;
  /** Show the entity label inline on each row. Defaults to false. */
  showEntity?: boolean;
}

/**
 * Prose-style chronological activity timeline for the ADMIN feed (`/activity`).
 *
 * ⚠️ **This is no longer the per-record renderer.** Document detail pages use the docked panel
 * (`components/panel/ActivityDock`), which holds its own state. This component survives for the
 * cross-module admin page, which keeps its own filter card, its `showEntity` rows and its relative
 * timestamps — restyling that surface is a separate design question.
 *
 * ## Keyset, not offset — and the reason is not performance
 *
 * The feed orders by `(occurredAtUtc, id)` and `occurredAtUtc` is FACT time, stamped inside the mutation.
 * Under the async activity consumer a row can therefore be inserted BELOW a page already on screen. With
 * OFFSET that shifts the window by one and the boundary row comes back on the next page, which React
 * renders as a duplicate `key` — one row drawn twice, one of the two silently stale. A cursor addresses a
 * POSITION IN THE ORDERING rather than a row count, so a concurrent insert cannot move it.
 *
 * `appendUnique` is shared with the panel's reducer rather than reimplemented: the two feeds have the same
 * overlap hazard and there is no version of it that should be solved twice.
 */
export function ActivityList({ fetcher, emptyMessage, showEntity = false }: ActivityListProps) {
  const [items, setItems] = useState<ActivityLogDto[]>([]);
  const [cursor, setCursor] = useState<{ occurredAtUtc: string; id: number } | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ActivityLogDto | null>(null);

  // Any mutation that emits an activity row bumps this. ⚠️ Subscribed as a VALUE — a getter called in
  // render would be memoized by the React Compiler on its stable reference and serve stale UI.
  const refreshVersion = useActivityRefreshStore((s) => s.version);

  const load = useCallback(
    async (from: { occurredAtUtc: string; id: number } | null) => {
      setIsLoading(true);
      setError(null);
      try {
        const page = await fetcher(from);
        setItems((prev) => (from === null ? page.items : appendUnique(prev, page.items)));
        setHasMore(page.hasMore);
        setCursor(
          page.nextCursorOccurredAtUtc !== null && page.nextCursorId !== null
            ? { occurredAtUtc: page.nextCursorOccurredAtUtc, id: page.nextCursorId }
            : null,
        );
        // `??` — the count rides the first page only, so a "show more" must not null it out.
        setTotalCount((prev) => page.totalCount ?? prev);
      } catch (e) {
        // Additive: the rows already on screen stay readable. A failed "show more" that emptied the list
        // would turn a recoverable blip into a lost page.
        setError(e instanceof Error ? e.message : 'Failed to load activity');
      } finally {
        setIsLoading(false);
      }
    },
    [fetcher],
  );

  // Back to the first page whenever the query changes or a mutation lands.
  useEffect(() => {
    setTotalCount(null);
    void load(null);
  }, [load, refreshVersion]);

  if (error && items.length === 0) {
    return (
      <div className="mx-3 my-2 rounded-lg border border-destructive/30 bg-destructive/8 p-3">
        <p className="text-xs text-destructive">{error}</p>
        <Button variant="ghost" size="sm" className="mt-1.5 h-7 px-2 text-xs" onClick={() => void load(null)}>
          Retry
        </Button>
      </div>
    );
  }

  // Skeleton, not a spinner — UI gate #2. A full-view rotating indicator for a list is a violation, and
  // this one had been here since before the gate existed.
  if (isLoading && items.length === 0) {
    return <ListSkeleton rows={4} className="px-3 py-2" />;
  }

  // The shared primitive rather than a hand-rolled <p> — UI gate #1, and it keeps the `localEmptyStates`
  // scan metric honest.
  if (items.length === 0) {
    return <EmptyState title={emptyMessage ?? 'No activity yet.'} className="p-6" />;
  }

  return (
    <>
      {error && (
        <div className="mx-3 my-2 rounded-lg border border-destructive/30 bg-destructive/8 p-3">
          <p className="text-xs text-destructive">{error}</p>
          <Button variant="ghost" size="sm" className="mt-1.5 h-7 px-2 text-xs" onClick={() => void load(cursor)}>
            Retry
          </Button>
        </div>
      )}

      <ul className="space-y-0.5">
        {items.map((item) => (
          <ActivityRow
            key={item.id}
            entry={item}
            showEntity={showEntity}
            onSelect={() => setSelected(item)}
          />
        ))}
      </ul>

      {hasMore && cursor && (
        <div className="px-3 pb-1 pt-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={isLoading}
            onClick={() => void load(cursor)}
          >
            {/* No "(N remaining)". That number came from subtracting a loaded count off a total that may
                already be stale, and it was wrong the moment a row arrived between two pages. */}
            {isLoading ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}

      {totalCount !== null && (
        <p className="px-3 pb-2 pt-1 text-[11px] text-muted-foreground">
          Showing <span className="tabular-nums">{items.length}</span> of{' '}
          <span className="tabular-nums">{totalCount}</span>
        </p>
      )}

      <ActivityDetailDrawer
        entry={selected}
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      />
    </>
  );
}
